import React, { useMemo } from 'react'
import DataGridComponent from '@components/common/DataGridComponent/DataGridComponent'
import { ColumnModel } from '@syncfusion/ej2-react-grids'
import { ITreatmentPlan, IPhaseItems } from '@dataTypes/treatmentPlan.types'
import { getFullName } from '@utils/fullname.util'
import { formatNumber } from '@utils/numberFormatter.util'
import { ClipMode } from '@constants/Common/GridComponent.contants'
import { useClinicChartingPreferences } from '@hooks/useClinicChartingPreferences'
import { filterTxPlanInsuranceEstimateColumns } from '@utils/tx-plan-procedure-grid-columns.util'
import {
  getTSFromToothSurfaces,
  parseSurfaceString,
} from '@utils/treatment-plan.utils'
import { formatToothSurfaceEntriesForDisplay } from '@utils/procedure-surface-selection.utils'

interface TreatmentPlanPrintViewProps {
  plan: ITreatmentPlan
  isPrint?: boolean
}

const TreatmentPlanPrintView: React.FC<TreatmentPlanPrintViewProps> = ({
  plan,
  isPrint,
}) => {
  const { showInsuranceEstimate } = useClinicChartingPreferences()

  const total = plan.phases?.reduce(
    (acc, phase) =>
      phase.items.reduce(
        (sum, item) => ({
          charges: sum.charges + (item.charges ?? 0),
          allowed_amount: sum.allowed_amount + (item.allowed_amount ?? 0),
          write_off: sum.write_off + (item.write_off ?? 0),
          adjustments: sum.adjustments + (item.adjustments ?? 0),
          insurance: sum.insurance + (item.insurance ?? 0),
          outstanding: sum.outstanding + (item.outstanding ?? 0),
        }),
        acc,
      ),
    {
      charges: 0,
      allowed_amount: 0,
      write_off: 0,
      adjustments: 0,
      insurance: 0,
      outstanding: 0,
    },
  )

  const columns = useMemo((): ColumnModel[] => {
    const builtColumns: ColumnModel[] = [
      {
        field: 'code',
        headerText: 'Code',
        allowSorting: true,
        width: '8%',
      },
      {
        field: 'description',
        headerText: 'Description',
        allowSorting: true,
        clipMode: ClipMode.EllipsisWithTooltip,
        width: '25%',
      },
      {
        field: 'surface',
        headerText: 'T/S',
        width: '8%',
        template: (props: IPhaseItems & { surface?: string | null }) => {
          // Prioritize tooth_surfaces over surface string
          const toothSurfaceEntries = getTSFromToothSurfaces(
            props.tooth_surfaces,
          )
          const surfaceEntries = parseSurfaceString(props.surface)
          const parsedEntries =
            toothSurfaceEntries.length > 0
              ? toothSurfaceEntries
              : surfaceEntries
          if (parsedEntries.length === 0) return <span>-</span>

          return (
            <div className="template-cell-tooth-surface">
              {formatToothSurfaceEntriesForDisplay(parsedEntries)}
            </div>
          )
        },
      },
      {
        field: 'charges',
        headerText: 'Clinic fee',
        allowSorting: true,
        width: '7%',
        template: (props: IPhaseItems & { charges?: number }) => {
          return formatNumber(props.charges ?? 0, true)
        },
      },
      {
        field: 'allowed_amt',
        headerText: 'Allowed amt.',
        clipMode: ClipMode.EllipsisWithTooltip,
        width: '7%',
        template: (props: IPhaseItems & { allowed_amount?: number | null }) => (
          <span>{formatNumber(props.allowed_amount ?? 0, true)}</span>
        ),
      },
      {
        field: 'write_off',
        headerText: 'Write-off',
        clipMode: ClipMode.EllipsisWithTooltip,
        width: '7%',
        template: (props: IPhaseItems & { write_off?: number | null }) => (
          <span>{formatNumber(props.write_off ?? 0, true)}</span>
        ),
      },
      {
        field: 'adjustments',
        headerText: 'Discount',
        clipMode: ClipMode.EllipsisWithTooltip,
        width: '7%',
        template: (props: IPhaseItems & { adjustments?: number }) => (
          <span>{formatNumber(props.adjustments ?? 0, true)}</span>
        ),
      },
      {
        field: 'insurance',
        headerText: 'Est. ins. pay',
        clipMode: ClipMode.EllipsisWithTooltip,
        width: '7%',
        template: (props: IPhaseItems & { insurance?: number }) => (
          <span>{formatNumber(props.insurance ?? 0, true)}</span>
        ),
      },
      {
        field: 'outstanding',
        headerText: 'Est. pt. res.',
        clipMode: ClipMode.EllipsisWithTooltip,
        width: '7%',
        template: (props: IPhaseItems & { outstanding?: number }) => (
          <span>{formatNumber(props.outstanding ?? 0, true)}</span>
        ),
      },
    ]
    return filterTxPlanInsuranceEstimateColumns(
      builtColumns,
      showInsuranceEstimate,
    )
  }, [showInsuranceEstimate])

  return (
    <div className="treatment-plan-print-view">
      <div className="tppv-bg">
        <h6 className="p-1 pt-2 mb-1">
          {getFullName(plan.patient)} – {plan.tp_heading}
        </h6>

        {!!plan &&
          plan.phases?.map((phase) => (
            <div key={phase.phase_name}>
              <div className="tppv-phase-bg">
                <strong>{phase.phase_name}</strong> –{' '}
                {phase.scheduled_date
                  ? new Date(phase.scheduled_date).toLocaleDateString()
                  : 'Unscheduled'}
              </div>
              <DataGridComponent
                columns={columns}
                data={phase.items
                  .map((p) => ({
                    ...p,
                    code: p.procedure_settings?.procedure?.procedure_code,
                    description: p.procedure_settings?.procedure?.description,
                    allowed_amt: p.allowed_amount ?? 0,
                    write_off: p.write_off ?? 0,
                  }))
                  .filter((item) => !item.is_hidden)}
                allowPaging={false}
                allowSorting={false}
                allowTextWrap={true}
              />
            </div>
          ))}
      </div>
      <div>
        <h6 className="mt-2 mb-1">Notes</h6>
        <div>{plan.notes?.[0]?.body}</div>
      </div>
      {!isPrint && (
        <div className="mt-3" style={{ fontSize: '14px' }}>
          <h6 className="mt-2 mb-1">Plan Pricing</h6>
          <div>
            <div className="d-flex justify-content-between">
              <span>Total Charges</span>
              <span>{formatNumber(total?.charges ?? 0, true)}</span>
            </div>
            <div className="d-flex justify-content-between">
              <span>Discount</span>
              <span>{formatNumber(total?.adjustments ?? 0, true)}</span>
            </div>
            {showInsuranceEstimate && (
              <>
                <div className="d-flex justify-content-between">
                  <span>Estimated insurance pay</span>
                  <span>{formatNumber(total?.insurance ?? 0, true)}</span>
                </div>
                <hr />
                <div className="d-flex justify-content-between fw-bold">
                  <span>Estimated patient responsibility</span>
                  <span>{formatNumber(total?.outstanding ?? 0, true)}</span>
                </div>
              </>
            )}
            <div className="mt-4" style={{ fontSize: '13px' }}>
              <strong>Patient Signature:</strong> __________________
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default TreatmentPlanPrintView
